(() => {
  if (window.__figaretCalendlyPopupLinksInitialized) return;
  window.__figaretCalendlyPopupLinksInitialized = true;

  const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
  const CALENDLY_CSS_URL = 'https://assets.calendly.com/assets/external/widget.css';

  const defaultConfig = {
    enabled: true,
    allowedHosts: ['calendly.com', 'www.calendly.com'],
    nativeParam: 'cld_native',
    nativeParamValue: '1',
    nativeAttribute: 'data-calendly-native',
    nativeAttributeValue: 'true',
  };

  const rawConfig = window.figaretCalendly || {};
  const config = {
    ...defaultConfig,
    ...rawConfig,
    allowedHosts: Array.isArray(rawConfig.allowedHosts)
      ? rawConfig.allowedHosts
      : defaultConfig.allowedHosts,
  };

  if (!config.enabled) return;

  const allowedHosts = new Set(
    config.allowedHosts.map((host) => String(host).trim().toLowerCase())
  );

  let calendlyScriptPromise = null;
  let calendlyCssLoaded = false;

  function loadCalendlyCss() {
    if (calendlyCssLoaded) return;

    const existingLink = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], link[data-calendly-widget-css="true"]')
    ).find((link) => {
      const href = link.getAttribute('href') || '';
      return link.dataset.calendlyWidgetCss === 'true' || href.indexOf(CALENDLY_CSS_URL) !== -1;
    });
    if (existingLink) {
      calendlyCssLoaded = true;
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CALENDLY_CSS_URL;
    link.dataset.calendlyWidgetCss = 'true';

    document.head.appendChild(link);
    calendlyCssLoaded = true;
  }

  function loadCalendlyScript() {
    if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
      return Promise.resolve(window.Calendly);
    }

    if (calendlyScriptPromise) return calendlyScriptPromise;

    calendlyScriptPromise = new Promise((resolve, reject) => {
      const onLoad = () => {
        if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
          resolve(window.Calendly);
          return;
        }

        reject(new Error('Calendly widget loaded without popup API.'));
      };

      const onError = () => reject(new Error('Calendly widget script failed to load.'));

      const existingScript = Array.from(
        document.querySelectorAll('script[src], script[data-calendly-widget-script="true"]')
      ).find((scriptTag) => {
        const src = scriptTag.getAttribute('src') || '';
        return (
          scriptTag.dataset.calendlyWidgetScript === 'true' ||
          src.indexOf(CALENDLY_SCRIPT_URL) !== -1
        );
      });
      if (existingScript) {
        existingScript.addEventListener('load', onLoad, { once: true });
        existingScript.addEventListener('error', onError, { once: true });

        if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
          resolve(window.Calendly);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = CALENDLY_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      script.dataset.calendlyWidgetScript = 'true';
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      document.body.appendChild(script);
    }).catch((error) => {
      calendlyScriptPromise = null;
      throw error;
    });

    return calendlyScriptPromise;
  }

  function getAnchor(event) {
    if (!(event.target instanceof Element)) return null;
    return event.target.closest('a[href]');
  }

  function isCalendlyHost(url) {
    if (!url || !url.hostname) return false;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    return allowedHosts.has(url.hostname.toLowerCase());
  }

  function hasNativeOptOut(anchor, url) {
    const nativeAttributeName = String(config.nativeAttribute || defaultConfig.nativeAttribute);
    const nativeAttributeValue = String(
      config.nativeAttributeValue || defaultConfig.nativeAttributeValue
    ).toLowerCase();
    const nativeParam = String(config.nativeParam || defaultConfig.nativeParam);
    const nativeParamValue = String(
      config.nativeParamValue || defaultConfig.nativeParamValue
    );

    const attributeValue = (anchor.getAttribute(nativeAttributeName) || '').toLowerCase();
    if (attributeValue === nativeAttributeValue) return true;

    return url.searchParams.get(nativeParam) === nativeParamValue;
  }

  function sanitizeCalendlyUrl(url) {
    const nativeParam = String(config.nativeParam || defaultConfig.nativeParam);
    const sanitizedUrl = new URL(url.toString());
    sanitizedUrl.searchParams.delete(nativeParam);
    return sanitizedUrl.toString();
  }

  function navigateNatively(anchor) {
    const href = anchor.href;
    const target = (anchor.getAttribute('target') || '').trim();

    if (target && target.toLowerCase() !== '_self') {
      const features = target.toLowerCase() === '_blank' ? 'noopener' : undefined;
      window.open(href, target, features);
      return;
    }

    window.location.assign(href);
  }

  // Delegated click handler ensures links added by theme editor/sections are covered.
  document.addEventListener('click', (event) => {
    const anchor = getAnchor(event);
    if (!anchor) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (anchor.hasAttribute('download')) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (error) {
      return;
    }

    if (!isCalendlyHost(url)) return;
    if (hasNativeOptOut(anchor, url)) return;

    event.preventDefault();
    const sanitizedUrl = sanitizeCalendlyUrl(url);

    loadCalendlyCss();
    loadCalendlyScript()
      .then((Calendly) => {
        Calendly.initPopupWidget({ url: sanitizedUrl });
      })
      .catch(() => {
        navigateNatively(anchor);
      });
  });
})();
